/*
 * Cart drawer upsell — "Måske du også vil kunne lide"
 * Loads Shopify product recommendations (complementary, falling back to related)
 * for the most recently added cart item, and adds them to the cart in one click.
 *
 * Built for Dawn. No dependencies beyond Dawn's own cart-drawer element.
 */
class CartUpsell extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded === 'true' || !this.dataset.productId) return;

    this.drawer = this.closest('cart-drawer');

    // Only fetch once the drawer is actually open, so we don't add a request to every page load.
    if (!this.drawer || this.drawer.classList.contains('active')) {
      this.load();
      return;
    }

    this.observer = new MutationObserver(() => {
      if (this.drawer.classList.contains('active')) {
        this.observer.disconnect();
        this.load();
      }
    });
    this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  async load() {
    if (this.dataset.loaded === 'true') return;
    this.dataset.loaded = 'true';

    for (const intent of ['complementary', 'related']) {
      try {
        const markup = await this.fetchIntent(intent);
        if (markup) {
          this.innerHTML = markup;
          this.classList.add('cart-upsell--visible');
          this.bindButtons();
          await this.applySubscriptionMode();
          return;
        }
      } catch (error) {
        console.error('[cart-upsell]', error);
      }
    }
  }

  async fetchIntent(intent) {
    const base = this.dataset.url || '/recommendations/products';
    const url = `${base}?section_id=${this.dataset.sectionId}&product_id=${this.dataset.productId}&limit=${
      this.dataset.limit || 4
    }&intent=${intent}`;

    const response = await fetch(url);
    if (!response.ok) return null;

    const text = await response.text();
    const inner = new DOMParser().parseFromString(text, 'text/html').querySelector('.cart-upsell__inner');
    if (!inner || Number(inner.dataset.count || 0) === 0) return null;

    return inner.outerHTML;
  }

  /*
   * If the cart already contains a subscription item, show the recommended
   * products on their matching subscription plan too — discounted price, a
   * short label, and an add-to-cart that carries the selling plan along.
   * Products without a subscription plan are left as one-time purchases.
   */
  async applySubscriptionMode() {
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';

    let cart;
    try {
      cart = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    } catch (error) {
      return;
    }

    const subscribed = (cart.items || []).find((item) => item.selling_plan_allocation);
    if (!subscribed) return;

    const plan = subscribed.selling_plan_allocation.selling_plan || {};
    const wantedOption = plan.options && plan.options[0] ? plan.options[0].value : null;

    this.querySelectorAll('.cart-upsell__card').forEach((card) => {
      // Only cards with a real add button — a multi-variant product links to
      // its product page instead, so showing a plan price there would mislead.
      const button = card.querySelector('.cart-upsell__button[data-variant-id]');
      if (!button) return;

      const source = card.querySelector('.cart-upsell__plans');
      if (!source) return;

      let plans;
      try {
        plans = JSON.parse(source.textContent);
      } catch (error) {
        return;
      }
      if (!plans || !plans.length) return;

      /*
       * Same delivery frequency as the cart, else the product's first plan.
       * Plan names are translated per product ("Deliver every 8 weeks" vs
       * "Levering hver 8. uge"), so an exact string match can miss — compare
       * the numbers in the option as a second pass before falling back.
       */
      const digits = (value) => (value || '').replace(/\D+/g, '');
      const match =
        (wantedOption && plans.find((p) => p.option === wantedOption)) ||
        (digits(wantedOption) && plans.find((p) => digits(p.option) === digits(wantedOption))) ||
        plans[0];

      const priceEl = card.querySelector('.cart-upsell__price');
      const nowEl = card.querySelector('.cart-upsell__price-now');
      const wasEl = card.querySelector('.cart-upsell__price-was');
      const oneTime = Number(priceEl && priceEl.dataset.onetime) || 0;
      const discounted = oneTime > 0 && match.price < oneTime;

      if (nowEl && match.priceText) {
        if (wasEl && discounted) {
          wasEl.textContent = nowEl.textContent.trim();
          wasEl.hidden = false;
        }
        nowEl.textContent = match.priceText;
      }

      const label = card.querySelector('.cart-upsell__plan');
      if (label) {
        const percent = discounted ? Math.round((1 - match.price / oneTime) * 100) : 0;
        label.textContent = percent ? `Abonnement · spar ${percent}%` : 'Abonnement';
        label.hidden = false;
      }

      button.dataset.sellingPlan = match.id;
      card.classList.add('cart-upsell__card--subscription');
    });
  }

  bindButtons() {
    this.querySelectorAll('.cart-upsell__button[data-variant-id]').forEach((button) => {
      button.addEventListener('click', () => this.addToCart(button));
    });
  }

  async addToCart(button) {
    if (button.classList.contains('is-loading')) return;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    this.hideError();

    const sections = this.drawer && typeof this.drawer.getSectionsToRender === 'function'
      ? this.drawer.getSectionsToRender().map((section) => section.id)
      : ['cart-drawer', 'cart-icon-bubble'];

    const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

    // Carries the subscription along when applySubscriptionMode() set one.
    const item = { id: Number(button.dataset.variantId), quantity: 1 };
    if (button.dataset.sellingPlan) item.selling_plan = Number(button.dataset.sellingPlan);

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: [item],
          sections,
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();

      if (data.status) {
        this.showError(data.description || data.message);
        return;
      }

      // Let Dawn's other cart components know something changed (guarded — these
      // are theme globals that may not exist in every Dawn version).
      try {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-upsell', cartData: data });
        }
      } catch (e) {
        /* no-op */
      }

      if (this.drawer && typeof this.drawer.renderContents === 'function') {
        // Re-renders the whole drawer; this element is replaced and reloads itself
        // with recommendations for the newly added product.
        this.drawer.renderContents(data);
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('[cart-upsell]', error);
      this.showError('Noget gik galt. Prøv igen.');
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  }

  showError(message) {
    const element = this.querySelector('.cart-upsell__error');
    if (!element) return;
    element.textContent = message;
    element.hidden = false;
  }

  hideError() {
    const element = this.querySelector('.cart-upsell__error');
    if (!element) return;
    element.hidden = true;
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
